import { useEffect, useState } from 'react'
import { useMap } from 'react-leaflet'
import styles from './styles.module.scss'

const MIN_SCALE_WIDTH_PX = 120
const MAX_SCALE_WIDTH_PX = 220
const TARGET_SCALE_WIDTH_RATIO = 0.18

function getNiceDistance(maxMeters: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(maxMeters))
  const normalized = maxMeters / magnitude

  if (normalized >= 10) return 10 * magnitude
  if (normalized >= 5) return 5 * magnitude
  if (normalized >= 3) return 3 * magnitude
  if (normalized >= 2) return 2 * magnitude
  return magnitude
}

function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${meters / 1000} km`
  }

  return `${meters} m`
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

interface IScaleState {
  distanceMeters: number
  widthPx: number
}

const EMITTER_DRAWER_WIDTH = 500
const SCALE_MARGIN_RIGHT = 10

const DistanceScale: React.FC<{ drawerExpanded: boolean }> = ({ drawerExpanded }) => {
  const map = useMap()
  const [scale, setScale] = useState<IScaleState>({ distanceMeters: 100000, widthPx: 160 })

  useEffect(() => {
    const syncScale = () => {
      const { x } = map.getSize()
      const centerPoint = map.latLngToContainerPoint(map.getCenter())
      const targetWidthPx = clamp(x * TARGET_SCALE_WIDTH_RATIO, MIN_SCALE_WIDTH_PX, MAX_SCALE_WIDTH_PX)
      const rightPoint = map.containerPointToLatLng([centerPoint.x + targetWidthPx, centerPoint.y])
      const metersAtTargetWidth = map.distance(map.getCenter(), rightPoint)
      const distanceMeters = getNiceDistance(metersAtTargetWidth)
      const actualWidthPx = (distanceMeters / metersAtTargetWidth) * targetWidthPx

      setScale({
        distanceMeters,
        widthPx: Math.round(actualWidthPx),
      })
    }

    syncScale()
    map.on('move zoom resize', syncScale)

    return () => {
      map.off('move zoom resize', syncScale)
    }
  }, [map])

  const rightOffset = drawerExpanded ? EMITTER_DRAWER_WIDTH + SCALE_MARGIN_RIGHT : SCALE_MARGIN_RIGHT

  return (
    <div className={styles.distanceScaleOverlay} style={{ right: rightOffset }}>
      <div className={styles.distanceScaleCard}>
        <div className={styles.distanceScaleTitle}>Distance at map center</div>
        <div className={styles.distanceScaleBarWrap}>
          <div className={styles.distanceScaleBar} style={{ width: `${scale.widthPx}px` }}>
            <span className={`${styles.distanceScaleTick} ${styles.distanceScaleTickStart}`} />
            <span className={`${styles.distanceScaleTick} ${styles.distanceScaleTickMid}`} />
            <span className={`${styles.distanceScaleTick} ${styles.distanceScaleTickEnd}`} />
          </div>
        </div>
        <div className={styles.distanceScaleLabels} style={{ width: `${scale.widthPx}px` }}>
          <span>0</span>
          <span>{formatDistance(scale.distanceMeters / 2)}</span>
          <span>{formatDistance(scale.distanceMeters)}</span>
        </div>
      </div>
    </div>
  )
}

export default DistanceScale
